import React, { useEffect, useState } from 'react';
import { useAuthStore } from '../../../store/authStore';
import { useConfigStore } from '../../../store/configStore';
import { PlusIcon, PencilIcon, TrashIcon, ArrowPathIcon, FunnelIcon } from '@heroicons/react/24/outline';

export default function LegacyTrain() {
  const { hasPermission } = useAuthStore();
  const { trains, stations, isLoading, error, fetchTrains, fetchStations, createTrain, updateTrain, deleteTrain, clearError } = useConfigStore();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTrain, setEditingTrain] = useState(null);
  const [filterDate, setFilterDate] = useState('');
  const [filterState, setFilterState] = useState('');
  const [formData, setFormData] = useState({
    s_seq_num: '',
    rail_code: '',
    state: 1,
    sdate: '',
  });

  const canWrite = hasPermission('config:write');

  useEffect(() => {
    fetchStations();
  }, []);

  useEffect(() => {
    const params = {};
    if (filterDate) params.sdate = filterDate;
    if (filterState !== '') params.state = parseInt(filterState);
    fetchTrains(params);
  }, [filterDate, filterState]);

  const handleOpenModal = (train = null) => {
    if (train) {
      setEditingTrain(train);
      setFormData({
        s_seq_num: train.s_seq_num,
        rail_code: train.rail_code,
        state: train.state,
        sdate: train.sdate,
      });
    } else {
      setEditingTrain(null);
      setFormData({
        s_seq_num: stations[0]?.seq_num || '',
        rail_code: '',
        state: 1,
        sdate: new Date().toISOString().split('T')[0],
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingTrain(null);
    setFormData({ s_seq_num: '', rail_code: '', state: 1, sdate: '' });
    clearError();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        s_seq_num: parseInt(formData.s_seq_num),
        state: parseInt(formData.state),
      };

      if (editingTrain) {
        await updateTrain(editingTrain.seq_num, data);
      } else {
        await createTrain(data);
      }
      handleCloseModal();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleDelete = async (seqNum) => {
    if (window.confirm('정말 삭제하시겠습니까?')) {
      try {
        await deleteTrain(seqNum);
      } catch (err) {
        alert(err.message);
      }
    }
  };

  const getStateBadge = (state) => {
    const stateMap = {
      0: { label: '비활성', class: 'bg-gray-100 text-gray-800' },
      1: { label: '활성', class: 'bg-green-100 text-green-800' },
      2: { label: '대기', class: 'bg-yellow-100 text-yellow-800' },
    };
    const stateInfo = stateMap[state] || { label: `상태 ${state}`, class: 'bg-gray-100 text-gray-800' };
    return (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${stateInfo.class}`}>
        {stateInfo.label}
      </span>
    );
  };

  return (
    <div className="py-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">열차정보관리</h1>
        {canWrite && (
          <button
            onClick={() => handleOpenModal()}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <PlusIcon className="w-5 h-5" />
            열차 정보 추가
          </button>
        )}
      </div>

      {/* 필터 영역 */}
      <div className="bg-white shadow rounded-lg p-4 mb-4">
        <div className="flex items-center gap-4">
          <FunnelIcon className="w-5 h-5 text-gray-400" />
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">날짜:</label>
            <input
              type="date"
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">상태:</label>
            <select
              value={filterState}
              onChange={(e) => setFilterState(e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">전체</option>
              <option value="0">비활성</option>
              <option value="1">활성</option>
              <option value="2">대기</option>
            </select>
          </div>
          <button
            onClick={() => {
              setFilterDate('');
              setFilterState('');
            }}
            className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900"
          >
            초기화
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
          <button onClick={clearError} className="ml-2 underline">닫기</button>
        </div>
      )}

      <div className="bg-white shadow rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <ArrowPathIcon className="w-8 h-8 text-gray-400 animate-spin" />
            <span className="ml-2 text-gray-500">로딩 중...</span>
          </div>
        ) : trains.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            등록된 열차 정보가 없습니다.
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  순번
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  열차 코드
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  정차역
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  운행일
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  상태
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  관리자
                </th>
                {canWrite && (
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    작업
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {trains.map((train) => (
                <tr key={train.seq_num}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {train.seq_num}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-mono font-medium text-gray-900">
                    {train.rail_code}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {train.station_name || `역 #${train.s_seq_num}`}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {train.sdate}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStateBadge(train.state)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {train.mng_id || '-'}
                  </td>
                  {canWrite && (
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => handleOpenModal(train)}
                          className="text-blue-600 hover:text-blue-900"
                          title="수정"
                        >
                          <PencilIcon className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(train.seq_num)}
                          className="text-red-600 hover:text-red-900"
                          title="삭제"
                        >
                          <TrashIcon className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={handleCloseModal} />

            <div className="inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg">
              <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">
                {editingTrain ? '열차 정보 수정' : '열차 정보 추가'}
              </h3>

              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    열차 코드 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.rail_code}
                    onChange={(e) => setFormData({ ...formData, rail_code: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="예: R000000001"
                    required
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    정차역 <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.s_seq_num}
                    onChange={(e) => setFormData({ ...formData, s_seq_num: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">정차역을 선택하세요</option>
                    {stations.map((station) => (
                      <option key={station.seq_num} value={station.seq_num}>
                        {station.station}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    운행일 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.sdate}
                    onChange={(e) => setFormData({ ...formData, sdate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    상태
                  </label>
                  <select
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="0">비활성</option>
                    <option value="1">활성</option>
                    <option value="2">대기</option>
                  </select>
                </div>

                <div className="flex justify-end gap-2 mt-6">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    취소
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    {isLoading ? '처리 중...' : (editingTrain ? '수정' : '추가')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
